import { FormControlLabel, FormGroup, Grid, Switch, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { ClassKeyOfStyles, StyledComponentProps, withStyles } from "@mui/styles";
import styles from "assets/jss/cardCreation.styles";
import MuiKeyboardDatePicker, { DatePickerMaxDate, DatePickerMinDate, DatePickerValidRange } from 'components/MuiExtras/MuiKeyboardDatePicker';
import { Formaters } from 'lib/Formarters';
import LibUtils from 'lib/LibUtils';
import { PublicationViewPriority, SystemRoles } from 'models/ApiEnums';
import { PublicationAdvancedOptionsDto } from 'models/ApiModels';
import moment from 'moment';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Session from 'services/Session';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface IInstitutionalBoxValidation {
    period?: boolean;
    hightLightUntil?: boolean;
}

interface IDataProps {
    advOptions: PublicationAdvancedOptionsDto;
    validation: IInstitutionalBoxValidation;
}

interface IProps extends IDataProps {
    onUpdateState: (data: IDataProps) => void;
}

type Props = IProps & StyledComponentProps<ClassKeyOfStyles<typeof styles>> & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class PublicationAdvancedOptions extends React.Component<Props> {

    ///
    private updateState() {
        const { advOptions: { enabled, viewHighlightUntil, viewPeriodEnd, viewPeriodStart }, validation } = this.props;

        validation.period = validation.hightLightUntil = true;

        if (enabled) {
            if (
                (viewPeriodStart != null && !LibUtils.isValidDate(viewPeriodStart))
                || (viewPeriodEnd != null && !LibUtils.isValidDate(viewPeriodEnd))
                || (viewPeriodStart != null && viewPeriodEnd != null && viewPeriodStart > viewPeriodEnd)
                || (viewPeriodStart != null && DatePickerValidRange(viewPeriodStart))
                || (viewPeriodEnd != null && DatePickerValidRange(viewPeriodEnd))
            ) {
                validation.period = false;
            }

            if (
                (viewHighlightUntil != null && !LibUtils.isValidDate(viewHighlightUntil))
                || (viewHighlightUntil != null && viewPeriodStart != null && viewPeriodStart > viewHighlightUntil)
                || (viewHighlightUntil != null && viewPeriodEnd != null && viewPeriodEnd < viewHighlightUntil)
                || (viewHighlightUntil != null && DatePickerValidRange(viewHighlightUntil))
            ) {
                validation.hightLightUntil = false;
            }
        }

        this.props.onUpdateState(this.props);
    }

    ///
    public render() {

        if (!Session.hasRole(SystemRoles.PublisherAdvancedOptions) && !Session.hasRole(SystemRoles.PublicationManager))
            return null;

        const { advOptions, validation, classes } = this.props;

        return (
            <>
                <FormGroup row>
                    <FormControlLabel
                        control={
                            <Switch
                                name="enabled"
                                checked={advOptions.enabled}
                                onChange={(e) => {
                                    var checked = (e.target as HTMLInputElement).checked;
                                    this.props.advOptions.enabled = checked;
                                    if (checked && this.props.advOptions.viewPriority == null) this.props.advOptions.viewPriority = PublicationViewPriority.None;
                                    this.updateState();
                                }}
                                value="enabled"
                                color="secondary"
                            />
                        }
                        label={this.props.t<string>("Views.Publication.Components.PublicationAdvancedOptions.EnabledLabel")}
                    />
                </FormGroup>

                {
                    advOptions.enabled
                        ? (
                            <FormGroup row>

                                <Table className={classes.highlightBox}>
                                    <TableBody>
                                        <TableRow style={{ verticalAlign: "top" }}>
                                            <TableCell width="50%">
                                                <Typography variant="button">{this.props.t("Views.Publication.Components.PublicationAdvancedOptions.DisplayPeriodText")}</Typography>
                                                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                                    <Grid item xs>
                                                        <MuiKeyboardDatePicker
                                                            autoFocus
                                                            fullWidth
                                                            clearable
                                                            title={this.props.t("Views.Publication.Components.PublicationAdvancedOptions.StartRangeTitle")}
                                                            label={Formaters.capitalize(this.props.t("Common.Literals.DateRangeStartLabel"))}
                                                            error={validation.period === false}
                                                            value={advOptions.viewPeriodStart}
                                                            maxDate={moment(advOptions.viewPeriodEnd || DatePickerMaxDate)}
                                                            onChange={(date) => {
                                                                if (date != null) {
                                                                    date.set({ hour: 0, minute: 0, second: 0 });
                                                                    advOptions.viewPeriodStart = date.toDate();
                                                                }
                                                                else {
                                                                    advOptions.viewPeriodStart = null;
                                                                }
                                                                this.updateState();
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs>
                                                        <MuiKeyboardDatePicker
                                                            fullWidth
                                                            clearable
                                                            title={this.props.t("Views.Publication.Components.PublicationAdvancedOptions.EndRangeTitle")}
                                                            label={Formaters.capitalize(this.props.t("Common.Literals.DateRangeEndLabel"))}
                                                            minDate={moment(advOptions.viewPeriodStart || DatePickerMinDate)}
                                                            value={advOptions.viewPeriodEnd}
                                                            onChange={
                                                                (date) => {

                                                                    if (date != null) {
                                                                        date.set({ hour: 23, minute: 59, second: 59 });
                                                                        advOptions.viewPeriodEnd = date.toDate();
                                                                    }
                                                                    else {
                                                                        advOptions.viewPeriodEnd = null;
                                                                    }
                                                                    this.updateState();
                                                                }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                            <TableCell width="50%">
                                                <FormGroup row>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="institutional"
                                                                checked={advOptions.isInstitutional}
                                                                onChange={(e) => {
                                                                    var checked = (e.target as HTMLInputElement).checked;
                                                                    this.props.advOptions.isInstitutional = checked;
                                                                    this.updateState();
                                                                }}
                                                                value="institutional"
                                                                color="secondary"
                                                            />
                                                        }
                                                        label={<b>{this.props.t("Views.Publication.Components.PublicationAdvancedOptions.InstitutionalLabel")}</b>}
                                                    />
                                                </FormGroup>
                                                <Typography variant="body1">
                                                    {this.props.t("Views.Publication.Components.PublicationAdvancedOptions.InstitutionalText")}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow style={{ verticalAlign: "top" }}>
                                            <TableCell>
                                                <Typography variant="button">{this.props.t("Views.Publication.Components.PublicationAdvancedOptions.HighlightPeriodText")}</Typography>

                                                <MuiKeyboardDatePicker
                                                    fullWidth
                                                    clearable
                                                    title={this.props.t("Views.Publication.Components.PublicationAdvancedOptions.HighlightPeriodTitle")}
                                                    label={Formaters.capitalize(this.props.t("Common.Literals.DateRangeEndLabel"))}
                                                    error={validation.hightLightUntil === false}
                                                    minDate={moment(advOptions.viewPeriodStart || DatePickerMinDate)}
                                                    maxDate={moment(advOptions.viewPeriodEnd || DatePickerMaxDate)}
                                                    value={advOptions.viewHighlightUntil}
                                                    onChange={(date) => {
                                                        if (date != null) {
                                                            date.set({ hour: 23, minute: 59, second: 59 });
                                                            advOptions.viewHighlightUntil = date.toDate();
                                                        }
                                                        else {
                                                            advOptions.viewHighlightUntil = null;
                                                        }
                                                        this.updateState();
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormGroup row>
                                                    <FormControlLabel
                                                        disabled={advOptions.viewPeriodStart != null || advOptions.viewPeriodEnd != null}
                                                        control={
                                                            <Switch
                                                                name="urgent"
                                                                checked={advOptions.viewPriority == PublicationViewPriority.High}
                                                                onChange={(e) => {
                                                                    advOptions.viewPriority = (e.target as HTMLInputElement).checked
                                                                        ? PublicationViewPriority.High
                                                                        : PublicationViewPriority.None;
                                                                    this.updateState();
                                                                }}
                                                                value="institutional"
                                                                color="secondary"
                                                            />
                                                        }
                                                        label={<b>{this.props.t("Views.Publication.Components.PublicationAdvancedOptions.UrgentLabel")}</b>}
                                                    />
                                                    <Typography variant="body1"  >
                                                        {this.props.t("Views.Publication.Components.PublicationAdvancedOptions.UrgentText")}
                                                    </Typography>
                                                </FormGroup>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>

                            </FormGroup >
                        )
                        : null
                }
            </>
        );
    }
}

export default withTranslation()(withStyles(styles)(PublicationAdvancedOptions));